import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';
import type { NostrEvent } from '@nostrify/nostrify';
import { getVideoAddress, type VideoData } from '@/lib/videoTypes';

export interface CustomEmoji {
  /** Emoji character, or ":shortcode:" for NIP-30 custom emojis. */
  value: string;
  /** NIP-30 image URL when value is a :shortcode:. */
  url?: string;
  count: number;
}

export interface ReactionCounts {
  likes: number;
  dislikes: number;
  custom: CustomEmoji[];
}

function reactionTargetFilters(video: VideoData, authorPubkey?: string) {
  const filters: { kinds: number[]; '#e'?: string[]; '#a'?: string[]; authors?: string[] }[] = [
    { kinds: [7], '#e': [video.id] },
  ];
  const address = getVideoAddress(video);
  if (address) {
    filters.push({ kinds: [7], '#a': [address] });
  }
  if (authorPubkey) {
    for (const f of filters) f.authors = [authorPubkey];
  }
  return filters;
}

function aggregateReactions(events: NostrEvent[]): ReactionCounts {
  // One reaction per user: keep each author's newest reaction only
  const byAuthor = new Map<string, NostrEvent>();
  for (const event of events) {
    const existing = byAuthor.get(event.pubkey);
    if (!existing || event.created_at > existing.created_at) {
      byAuthor.set(event.pubkey, event);
    }
  }

  let likes = 0;
  let dislikes = 0;
  const customMap = new Map<string, CustomEmoji>();

  for (const event of byAuthor.values()) {
    const content = event.content.trim();

    if (content === '+' || content === '' || content === '👍') {
      likes++;
    } else if (content === '-' || content === '👎') {
      dislikes++;
    } else if (content.length > 0) {
      // NIP-30: resolve :shortcode: to its emoji tag image
      let url: string | undefined;
      const shortcodeMatch = content.match(/^:([a-zA-Z0-9_+-]+):$/);
      if (shortcodeMatch) {
        const emojiTag = event.tags.find(
          t => t[0] === 'emoji' && t[1] === shortcodeMatch[1] && t[2],
        );
        url = emojiTag?.[2];
      }
      const key = url ? `${content}|${url}` : content;
      const existing = customMap.get(key);
      if (existing) {
        customMap.set(key, { ...existing, count: existing.count + 1 });
      } else {
        customMap.set(key, { value: content, url, count: 1 });
      }
    }
  }

  const custom = Array.from(customMap.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);

  return { likes, dislikes, custom };
}

/** Fetch all reactions for a video. */
export function useVideoReactions(video: VideoData | null) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['reactions', video?.id, video?.dTag],
    queryFn: async ({ signal }) => {
      if (!video) return { likes: 0, dislikes: 0, custom: [] } as ReactionCounts;

      const events = await nostr.query(
        reactionTargetFilters(video),
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );

      const unique = Array.from(new Map(events.map(e => [e.id, e])).values());
      return aggregateReactions(unique);
    },
    enabled: !!video,
  });
}

export interface UserReactionState {
  liked: boolean;
  disliked: boolean;
  custom: string[];
  events: NostrEvent[];
}

/** Fetch the logged-in user's reactions to a video. */
export function useUserReactions(video: VideoData | null) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['reactions', 'user', user?.pubkey, video?.id, video?.dTag],
    queryFn: async ({ signal }) => {
      const empty: UserReactionState = { liked: false, disliked: false, custom: [], events: [] };
      if (!video || !user) return empty;

      const events = await nostr.query(
        reactionTargetFilters(video, user.pubkey),
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );

      const state: UserReactionState = { liked: false, disliked: false, custom: [], events };
      for (const event of events) {
        const content = event.content.trim();
        if (content === '+' || content === '' || content === '👍') state.liked = true;
        else if (content === '-' || content === '👎') state.disliked = true;
        else if (content) state.custom.push(content);
      }
      return state;
    },
    enabled: !!video && !!user,
  });
}

export interface PostReactionInput {
  /** "+", "-", an emoji character, or ":shortcode:" for NIP-30. */
  content: string;
  /** NIP-30 custom emoji: shortcode + image URL. */
  emoji?: { shortcode: string; url: string };
}

/** Post (or replace) a NIP-25 reaction to a video. */
export function usePostReaction(video: VideoData | null) {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: PostReactionInput) => {
      if (!user) throw new Error('You must be logged in to react');
      if (!video) throw new Error('No video loaded');

      const tags: string[][] = [
        ['e', video.id],
        ['p', video.pubkey],
        ['k', String(video.originalEvent.kind)],
      ];

      const address = getVideoAddress(video);
      if (address) {
        tags.push(['a', address]);
      }

      if (input.emoji) {
        tags.push(['emoji', input.emoji.shortcode, input.emoji.url]);
      }

      await createEvent({ kind: 7, content: input.content, tags });
      return input;
    },
    onSuccess: (input) => {
      queryClient.invalidateQueries({ queryKey: ['reactions'] });
      const label = input.emoji ? `:${input.emoji.shortcode}:` : input.content;
      toast({ title: `Reacted with ${label}` });
    },
    onError: (error) => {
      toast({
        title: 'Failed to react',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}

/** Delete a reaction event (NIP-09 deletion request). */
export function useDeleteReaction() {
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventIds: string[]) => {
      await createEvent({
        kind: 5,
        content: 'Removing reaction',
        tags: eventIds.map(id => ['e', id]),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reactions'] });
    },
  });
}
